import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import studentService from "../../services/studentService";
import Pagination from "../../components/common/Pagination";
import ConfirmModal from "../../components/common/ConfirmModal";
import StudentView from "./StudentView";
import courseService from "../../services/courseService";
import { getYearOptions } from "../../utils/courseYears";

const STATUS_COLORS = {
  Active: { bg: "#e8f1f8", color: "#002747" },
  Passed: { bg: "#dcfce7", color: "#15803d" },
  Detained: { bg: "#fef9c3", color: "#92400e" },
  Dropped: { bg: "#f3f4f6", color: "#6b7280" },
  "Passed Out": { bg: "#f3f4f6", color: "#6b7280" },
};

const STATUS_OPTIONS = ["Active", "Passed", "Detained", "Dropped", "Passed Out"];
const GENDER_OPTIONS = ["Male", "Female", "Other"];
const ADMISSION_CATEGORY_OPTIONS = [
  "Open",
  "OBC",
  "SC",
  "ST",
  "NT-A",
  "NT-B",
  "NT-C",
  "NT-D",
  "VJ",
  "SBC",
  "TFWS",
  "Open Minority Scholarship",
  "EWS",
];

export default function StudentList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { academicYear, isClerk } = useApp();
  const basePath = isClerk ? "/clerk/students" : "/students";
  const viewStudentId = searchParams.get("view");

  const [students, setStudents] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [yearNumberFilter, setYearNumberFilter] = useState("");
  const [admissionCategoryFilter, setAdmissionCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    courseService
      .getAll({ isActive: true })
      .then((res) => setCourses(res.data || []))
      .catch((err) => toast.error("Couldn't load courses: " + err.message));
  }, [academicYear]);

  const yearOptions = useMemo(() => {
    const selectedCourse = courses.find((course) => course._id === courseFilter);
    if (selectedCourse) return getYearOptions(selectedCourse);
    return [];
  }, [courses, courseFilter]);

  const getCourseName = useCallback((courseVal) => {
    if (!courseVal) return "—";
    if (typeof courseVal === "object") {
      return courseVal.shortName || courseVal.courseName || "—";
    }
    const match = courses.find((c) => c._id === courseVal || c.shortName === courseVal || c.courseName === courseVal);
    if (match) return match.shortName || match.courseName;
    return courseVal || "—";
  }, [courses]);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await studentService.getAll({
        academicYear: academicYear?._id || "",
        search,
        page,
        limit: 15,
        status: statusFilter,
        gender: genderFilter,
        course: courseFilter,
        yearNumber: yearNumberFilter,
        admissionCategory: admissionCategoryFilter,
      });
      setStudents(res.data);
      setPagination(res.pagination);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    academicYear,
    search,
    page,
    statusFilter,
    genderFilter,
    courseFilter,
    yearNumberFilter,
    admissionCategoryFilter,
  ]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);
  useEffect(() => {
    setPage(1);
  }, [
    search,
    statusFilter,
    genderFilter,
    courseFilter,
    yearNumberFilter,
    admissionCategoryFilter,
    academicYear,
  ]);

  const clearFilters = () => {
    setStatusFilter("");
    setGenderFilter("");
    setCourseFilter("");
    setYearNumberFilter("");
    setAdmissionCategoryFilter("");
    setSearch("");
  };

  const hasActiveFilters = Boolean(
    search ||
    statusFilter ||
    genderFilter ||
    courseFilter ||
    yearNumberFilter ||
    admissionCategoryFilter,
  );

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await studentService.remove(deleteTarget._id);
      toast.success(`"${deleteTarget.fullName}" deleted`);
      setDeleteTarget(null);
      fetchStudents();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const getInitials = (name = "") =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "ST";

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Students</h1>
          <p className="page-subtitle">
            {academicYear
              ? `${pagination?.total ?? "…"} students — ${academicYear.label}`
              : "No academic year selected"}
          </p>
        </div>
        <Link to={`${basePath}/add`} className="btn btn-primary">+ Add Student</Link>
      </div>

      {!academicYear && (
        <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#92400e" }}>
          ⚠️ Select an academic year from the Dashboard to view students.
        </div>
      )}

      <div className="card">
        {/* Toolbar */}
        <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border)", display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          <div className="search-bar">
            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "#9ca3af", flexShrink: 0 }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              placeholder="Search by name, enrollment, mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="form-control"
            style={{ width: "160px", fontSize: 13 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            className="form-control"
            style={{ width: "140px", fontSize: 13 }}
            value={genderFilter}
            onChange={(e) => setGenderFilter(e.target.value)}
          >
            <option value="">All Genders</option>
            {GENDER_OPTIONS.map((gender) => (
              <option key={gender} value={gender}>{gender}</option>
            ))}
          </select>
          <select
            className="form-control"
            style={{ width: "150px", fontSize: 13 }}
            value={courseFilter}
            onChange={(e) => {
              setCourseFilter(e.target.value);
              setYearNumberFilter("");
            }}
          >
            <option value="">All Courses</option>
            {courses.map((course) => (
              <option key={course._id} value={course._id}>{course.shortName}</option>
            ))}
          </select>
          <select
            className="form-control"
            style={{ width: "150px", fontSize: 13 }}
            value={yearNumberFilter}
            onChange={(e) => setYearNumberFilter(e.target.value)}
            disabled={!courseFilter}
          >
            <option value="">All Course Years</option>
            {yearOptions.map((opt) => (
              <option key={opt.yearNumber} value={opt.yearNumber}>{opt.label}</option>
            ))}
          </select>
          <select
            className="form-control"
            style={{ width: "200px", fontSize: 13 }}
            value={admissionCategoryFilter}
            onChange={(e) => setAdmissionCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {ADMISSION_CATEGORY_OPTIONS.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          {hasActiveFilters && (
            <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="loading-center"><div className="spinner" />Loading students...</div>
        ) : students.length === 0 ? (
          <div className="empty-state">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p>{search ? "No students match your search." : "No students in this year."}</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Enrollment No.</th>
                  <th>Course</th>
                  <th>Status</th>
                  <th>Category</th>
                  <th>Mobile</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => {
                  const sc = STATUS_COLORS[s.status] || STATUS_COLORS.Active;
                  return (
                    <tr key={s._id}>
                      <td style={{ color: "var(--text-muted)", fontSize: 12 }}>
                        {(page - 1) * 15 + i + 1}
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                          <div style={{
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            overflow: "hidden",
                            background: "#e2e8f0",
                            color: "#475569",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}>
                            {s.studentPhoto?.url ? (
                              <img
                                src={s.studentPhoto.url}
                                alt={s.fullName}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              getInitials(s.fullName)
                            )}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{s.fullName}</div>
                            <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                              {s.gender} · DOB: {s.dob}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td><span className="badge badge-blue">{s.enrollmentNo}</span></td>
                      <td style={{ fontSize: 13 }}>
                        {getCourseName(s.course)}
                        {s.yearNumber && (
                          <span style={{ color: "var(--text-muted)", marginLeft: 4 }}>
                            {s.yearNumber}{s.yearNumber === 1 ? "st" : s.yearNumber === 2 ? "nd" : s.yearNumber === 3 ? "rd" : "th"} Year
                          </span>
                        )}
                      </td>
                      <td>
                        <span style={{ background: sc.bg, color: sc.color, padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 500 }}>
                          {s.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {s.admissionType && <span className="badge badge-green" style={{ marginRight: 4 }}>{s.admissionType}</span>}
                        <span style={{ color: "var(--text-muted)" }}>{s.admissionCategory}</span>
                      </td>
                      <td style={{ fontSize: 13 }}>{s.mobile}</td>
                      <td>
                        <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                          <button className="btn btn-outline btn-sm" onClick={() => navigate(`${basePath}/${s._id}`)}>View</button>
                          <button className="btn btn-outline btn-sm" onClick={() => navigate(`${basePath}/${s._id}/id-card`)}>ID Card</button>
                          <button className="btn btn-outline btn-sm" onClick={() => navigate(`${basePath}/${s._id}/edit`)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(s)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Pagination pagination={pagination} onPageChange={setPage} />
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Delete Student"
          message={`Delete "${deleteTarget.fullName}"? Blocked if certificates exist.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
}
